import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useState } from "react";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Spinner } from "reactstrap";
import * as Yup from "yup";
import { loginAPI } from "../../api/authAPIs";
import {
  Block,
  BlockContent,
  BlockHead,
  BlockTitle,
  Button,
  PreviewCard,
} from "../../components/Component";
import LogoDark from "../../images/logo-dark.png";
import Logo from "../../images/logo.png";
import Head from "../../layout/head/Head";
import AuthFooter from "./AuthFooter";

toast.configure();

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  
  const initialValues = {
    email: "",
    password: "",
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
  });

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await loginAPI({ body: values });
      const data = response.data;
      toast.success(data.message, { position: toast.POSITION.TOP_CENTER });
      localStorage.setItem("accessToken", data.data.token);
      navigate("/");
    } catch (error) {
      toast.error("Something went wrong. Please try again!", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Head title="Login" />
      <Block className="nk-block-middle nk-auth-body wide-xs">
        <div className="brand-logo pb-4 text-center">
          <Link to={process.env.PUBLIC_URL + "/"} className="logo-link">
            <img
              className="logo-light logo-img logo-img-lg"
              src={Logo}
              alt="logo"
            />
            <img
              className="logo-dark logo-img logo-img-lg"
              src={LogoDark}
              alt="logo-dark"
            />
          </Link>
        </div>
        <PreviewCard className="card-bordered" bodyClass="card-inner-lg">
          <BlockHead>
            <BlockContent>
              <BlockTitle tag="h4">Sign-In</BlockTitle>
            </BlockContent>
          </BlockHead>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form className="is-alter">
                <div className="form-group">
                  <div className="form-label-group">
                    <label className="form-label" htmlFor="email">
                      Email
                    </label>
                  </div>
                  <div className="form-control-wrap">
                    <Field
                      type="email"
                      name="email"
                      id="email"
                      placeholder="Enter your email address"
                      className="form-control-lg form-control"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="invalid"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <div className="form-label-group">
                    <label className="form-label" htmlFor="password">
                      Password
                    </label>
                    <Link
                      className="link link-primary link-sm"
                      to={`${process.env.PUBLIC_URL}/auth-reset`}
                    >
                      Forgot Password?
                    </Link>
                  </div>
                  <div className="form-control-wrap position-relative">
                    <Field
                      type={showPassword ? "text" : "password"}
                      name="password"
                      id="password"
                      placeholder="Enter your password"
                      className="form-control-lg form-control"
                    />
                    <span
                      className="password-toggle-icon"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", cursor: "pointer" }}
                    >
                      {showPassword ? <AiFillEyeInvisible size={20} /> : <AiFillEye size={20} />}
                    </span>
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="invalid"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <Button
                    size="lg"
                    className="btn-block"
                    type="submit"
                    color="primary"
                    disabled={loading || isSubmitting}
                  >
                    {loading ? <Spinner size="sm" color="light" /> : "Sign in"}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </PreviewCard>
      </Block>
      <AuthFooter />
    </>
  );
};

export default Login;
